import { AUTH_TYPES, TEMPLATE_CATEGORIES } from './constants.js'

/*
 * Slack incoming-webhook notifier for privacy request events.
 *
 * Distinct from the OAuth2 bot template in slack.js: one template = one auth type, and an
 * incoming webhook needs no runtime authentication at all - the secret is the webhook URL
 * itself, which the admin pastes over the endpointAddress placeholder below. That URL lands
 * unencrypted in the service configuration; treat it as a revocable, single-purpose credential.
 *
 * Categorised GENERIC (a notifier-style template); its endpoints are used by creating a Slack
 * Webhook system - notifier endpoints are not cross-listed on other systems. The endpoints
 * deliberately carry no url so the wizard pre-fills from defaults.endpointAddress.
 * Payloads use plain "text" (not Block Kit): literal token substitution favours flat strings.
 */
export const slackWebhookTemplate = {
	id: 'slack-webhook',
	guid: 'f7137135-de79-46b7-ad24-ba1017d2af9b',
	name: 'Slack Webhook',
	category: TEMPLATE_CATEGORIES.GENERIC,
	popularity: 'Popular',
	color: '#4a154b',
	icon: 'mdi-slack',
	description: 'Post privacy request event notifications to a Slack channel via an incoming webhook. Just paste your webhook URL, and treat it as a credential: anyone holding it can post to the channel.',
	features: ['Incoming Webhook', 'No Auth Setup', 'Per-Channel Routing', 'Deadline Escalation'],
	endpoints: [
		{
			name: 'Notify - New Privacy Request',
			method: 'POST',
			description: 'Post to the webhook channel when a new privacy request is submitted',
			cassieObjectID: -13,
			incrementField: 'ID',
			resetIncrementValue: false,
			fields: [
				{ name: 'RequestNumber', filter: '' },
				{ name: 'Reference', filter: '' },
				{ name: 'RequestTypeName', filter: '' }
			],
			requestBody: '{ "text": "New privacy request [PLACEHOLDER_REQUESTNUMBER] (reference [PLACEHOLDER_REFERENCE]) of type [PLACEHOLDER_REQUESTTYPENAME] was submitted." }',
			headers: [
				{ Name: 'Content-Type', value: 'application/json; charset=utf-8' }
			],
			successResponses: [{ code: 200, message: '' }]
		},
		{
			name: 'Notify - Data Subject Message',
			method: 'POST',
			description: 'Post to the webhook channel when a data subject writes from the request status page',
			cassieObjectID: -14,
			incrementField: 'ID',
			resetIncrementValue: false,
			fields: [
				{ name: 'RequestNumber', filter: '' },
				{ name: 'Reference', filter: '' },
				{ name: 'CommentText', filter: '' }
			],
			requestBody: '{ "text": "New message from the data subject on privacy request [PLACEHOLDER_REQUESTNUMBER] (reference [PLACEHOLDER_REFERENCE]):", "attachments": [{ "text": "[PLACEHOLDER_COMMENTTEXT]" }] }',
			headers: [
				{ Name: 'Content-Type', value: 'application/json; charset=utf-8' }
			],
			successResponses: [{ code: 200, message: '' }]
		},
		{
			name: 'Notify - Request/Task Nearing Deadline',
			method: 'POST',
			description: 'Post an escalation to the webhook channel when a privacy request or task enters its warning window',
			cassieObjectID: -16,
			incrementField: 'ID',
			resetIncrementValue: false,
			fields: [
				{ name: 'EventTitle', filter: '' },
				{ name: 'RequestNumber', filter: '' },
				{ name: 'Reference', filter: '' },
				{ name: 'DaysUntilDue', filter: '' }
			],
			requestBody: '{ "text": "[PLACEHOLDER_EVENTTITLE]: privacy request [PLACEHOLDER_REQUESTNUMBER] (reference [PLACEHOLDER_REFERENCE]) is due in [PLACEHOLDER_DAYSUNTILDUE] day(s)." }',
			headers: [
				{ Name: 'Content-Type', value: 'application/json; charset=utf-8' }
			],
			successResponses: [{ code: 200, message: '' }]
		},
		{
			name: 'Notify - Deadline Breached',
			method: 'POST',
			description: 'Post an escalation to the webhook channel when a privacy request or task passes its statutory deadline',
			cassieObjectID: -17,
			incrementField: 'ID',
			resetIncrementValue: false,
			fields: [
				{ name: 'EventTitle', filter: '' },
				{ name: 'RequestNumber', filter: '' },
				{ name: 'Reference', filter: '' },
				{ name: 'DaysOverdue', filter: '' }
			],
			requestBody: '{ "text": "[PLACEHOLDER_EVENTTITLE]: privacy request [PLACEHOLDER_REQUESTNUMBER] (reference [PLACEHOLDER_REFERENCE]) is [PLACEHOLDER_DAYSOVERDUE] day(s) overdue." }',
			headers: [
				{ Name: 'Content-Type', value: 'application/json; charset=utf-8' }
			],
			successResponses: [{ code: 200, message: '' }]
		},
		{
			name: 'Notify - Inactive Request',
			method: 'POST',
			description: 'Post to the webhook channel when a privacy request has gone inactive',
			cassieObjectID: -18,
			incrementField: 'ID',
			resetIncrementValue: false,
			fields: [
				{ name: 'RequestNumber', filter: '' },
				{ name: 'Reference', filter: '' },
				{ name: 'Reason', filter: '' },
				{ name: 'DaysSinceLastActivity', filter: '' }
			],
			requestBody: '{ "text": "Privacy request [PLACEHOLDER_REQUESTNUMBER] (reference [PLACEHOLDER_REFERENCE]) is inactive ([PLACEHOLDER_REASON]) - no activity for [PLACEHOLDER_DAYSSINCELASTACTIVITY] day(s)." }',
			headers: [
				{ Name: 'Content-Type', value: 'application/json; charset=utf-8' }
			],
			successResponses: [{ code: 200, message: '' }]
		}
	],
	defaults: {
		endpointAddress: 'https://hooks.slack.com/services/[YOUR_WEBHOOK_PATH]',
		authenticationTypeId: AUTH_TYPES.NO_AUTH,
		// Per-service throttle (ms) between outbound calls, distinct from the system poll cadence.
		// Slack rate-limits incoming webhooks, so ship a non-zero throttle by default; the first
		// sweep after deploy may burst a backlog of privacy events.
		requestThrottlingWaitTimeMS: 1000,
		notes: 'Slack incoming-webhook notifier for privacy request events. Paste your webhook URL over the endpoint address placeholder - the URL is the credential (stored unencrypted; revoke and regenerate it in Slack if exposed). Each webhook posts to the channel it was created for.'
	}
}
